/**
 * ConfirmActionModal — reusable confirmation dialog for the callbacks UI
 * (Story 7.13b AC5). Wraps the shadcn `<Dialog>` primitive with a body slot
 * for any page-specific extras (e.g. a "reason" input on the Cancel flow).
 *
 * Kept under `components/callbacks/` rather than `components/ui/` for now —
 * this is the first reusable confirm modal in the admin and the API will
 * stabilize once a second consumer arrives.
 */
import { type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface ConfirmActionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel: string;
  /** Forwarded to the confirm `<Button>` — e.g. `'destructive'` for cancel. */
  confirmVariant?: 'default' | 'destructive';
  pendingLabel?: string;
  isPending?: boolean;
  cancelLabel?: string;
  onConfirm: () => void;
  /** Optional extra body slot — e.g. a "reason" input above the footer. */
  children?: ReactNode;
}

export function ConfirmActionModal({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmVariant = 'default',
  pendingLabel,
  isPending = false,
  cancelLabel = 'Voltar',
  onConfirm,
  children,
}: ConfirmActionModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} disabled={isPending}>
            {isPending && pendingLabel ? pendingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
